import { KsService, useApiClient } from '@siclop/kstone-vue'
import type { WireframeComponent } from '../models'
import type { ComponentCategory } from '../enums'
import { WireframeComponentFilter } from '../filters/WireframeComponentFilter'

export class WireframeComponentService extends KsService {
  protected static mainUrl = '/wireframecomponents'

  /**
   * Get all active components (regardless of design system)
   */
  static async getAllActive(): Promise<WireframeComponent[]> {
    const filter = new WireframeComponentFilter()
    filter.isActive = true
    return await this.getAll(filter) as WireframeComponent[]
  }

  /**
   * Get all active components for a design system
   */
  static async getByDesignSystem(designSystemId: number): Promise<WireframeComponent[]> {
    const client = useApiClient()
    return await client.getAll<WireframeComponent>(`${this.mainUrl}/designsystem/${designSystemId}`)
  }

  /**
   * Get components grouped by category for a design system
   */
  static async getGroupedByCategory(designSystemId: number): Promise<Record<ComponentCategory, WireframeComponent[]>> {
    const client = useApiClient()
    return await client.get<Record<ComponentCategory, WireframeComponent[]>>(`${this.mainUrl}/designsystem/${designSystemId}/grouped`) ?? {} as Record<ComponentCategory, WireframeComponent[]>
  }

  /**
   * Get a component by code within a design system
   */
  static async getByCode(designSystemId: number, code: string): Promise<WireframeComponent | null> {
    const client = useApiClient()
    return await client.get<WireframeComponent>(`${this.mainUrl}/designsystem/${designSystemId}/code/${code}`) ?? null
  }

  /**
   * Increment the version of a component
   */
  static async incrementVersion(componentId: number): Promise<number> {
    const client = useApiClient()
    const axios = client.getAxiosInstance()
    const response = await axios.patch<number>(`${this.mainUrl}/${componentId}/increment-version`)
    return response.data
  }

  /**
   * Clone a component to another design system
   */
  static async cloneToDesignSystem(componentId: number, targetDesignSystemId: number, newCode?: string): Promise<WireframeComponent | null> {
    const client = useApiClient()
    return await client.postRaw<{ targetDesignSystemId: number; newCode?: string }, WireframeComponent>(
      `${this.mainUrl}/${componentId}/clone`,
      { targetDesignSystemId, newCode }
    )
  }
}
